/**
 * Media Orchestrator (ORC) — resolves registry sources to URLs and media kinds.
 * No API calls; local + remote URLs only. Extension-based classification for speed.
 */
import type { MediaEntry, MediaKind, ResolvedMedia } from './types';

const IMAGE_EXT = /\.(avif|bmp|gif|jpe?g|png|svg|webp|ico)$/i;
const VIDEO_EXT = /\.(mp4|webm|ogv|mov|m4v)$/i;
const AUDIO_EXT = /\.(mp3|wav|ogg|oga|m4a|aac|flac)$/i;

function inferKindFromSource(source: string, hint?: MediaKind): MediaKind {
  if (hint && hint !== 'unknown') return hint;
  const path = source.split('?')[0] ?? source;
  if (IMAGE_EXT.test(path)) return 'image';
  if (VIDEO_EXT.test(path)) return 'video';
  if (AUDIO_EXT.test(path)) return 'audio';
  return 'unknown';
}

function isRemoteUrl(source: string): boolean {
  return /^https?:\/\//i.test(source.trim());
}

/**
 * Resolve a registry source string to a usable URL in the browser.
 * - http(s): unchanged
 * - absolute path from site root: BASE_URL + path
 * - relative path: prefixed with / so it resolves from origin (public/)
 */
export function resolveMediaUrl(source: string): string {
  const s = source.trim();
  if (!s) return '';
  if (isRemoteUrl(s)) return s;
  const base = import.meta.env.BASE_URL || '/';
  if (s.startsWith('/')) {
    const normalizedBase = base.endsWith('/') ? base.slice(0, -1) : base;
    return `${normalizedBase}${s}`;
  }
  const withSlash = s.startsWith('./') ? s.slice(1) : `/${s}`;
  const normalizedBase = base.endsWith('/') ? base.slice(0, -1) : base;
  return `${normalizedBase}${withSlash}`;
}

export function orchestrateResolve(
  scope: string,
  variable: string,
  entry: MediaEntry
): ResolvedMedia {
  const url = resolveMediaUrl(entry.source);
  const thumbnailUrl = entry.thumbnail?.trim()
    ? resolveMediaUrl(entry.thumbnail)
    : undefined;
  const kind = inferKindFromSource(entry.source, entry.kindHint);

  return {
    variable,
    scope,
    url,
    thumbnailUrl,
    kind,
    alt: entry.alt,
    entry,
  };
}

/** Typical browser-supported range for `HTMLMediaElement.playbackRate` (spec allows wide span; engines clamp). */
const PLAYBACK_RATE_MIN = 0.0625;
const PLAYBACK_RATE_MAX = 16;

/**
 * Normalize registry `speed` for `HTMLMediaElement.playbackRate`.
 * Values below `1` slow playback; above `1` speed up. Invalid/missing values default to `1`.
 */
export function normalizeMediaPlaybackRate(speed: number | undefined): number {
  if (speed === undefined) return 1;
  if (typeof speed !== 'number' || !Number.isFinite(speed) || speed <= 0) {
    return 1;
  }
  return Math.min(PLAYBACK_RATE_MAX, Math.max(PLAYBACK_RATE_MIN, speed));
}
